'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import React from 'react';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { calculateReadingTime } from '../../utils/calculate-reading-time';
import { Heading } from '../ui/heading';

export type BlogCardProps = {
  blog: Blog;
};

export function BlogCard({ blog }: BlogCardProps) {
  const locale = useLocaleStore(state => state.currentLocale);

  const textContent = getTextFromHTML(blog.content);
  const { t } = useTranslations();

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <Link
      prefetch={false}
      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
      className="col-span-1 flex w-full flex-col items-start justify-start gap-4"
    >
      <div className="relative">
        {blog.categories && blog.categories.length > 0 && (
          <p className="absolute left-6 top-6 flex h-[30px] items-center justify-center bg-primary-foreground px-3 text-xs font-semibold uppercase">
            {blog.categories[0].name}
          </p>
        )}
        <Image
          src={blog?.image?.url}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          width={0}
          height={0}
          className="h-[260px] w-full object-cover object-center md:h-[290px]"
          sizes="100vw"
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-2">
        <div className="flex w-full items-center justify-start gap-2">
          <p className="whitespace-nowrap text-sm leading-[125%] text-[#666]">{formattedDate}</p>
          <p className="whitespace-nowrap text-sm leading-[125%] text-[#666]">|</p>
          <p className="whitespace-nowrap text-sm leading-[125%] text-[#666]">
            {calculateReadingTime(textContent)} min.
          </p>
        </div>
        <Heading tag="h6" as="h3" title={blog.name} className={{ title: 'line-clamp-2' }} />
      </div>
      <Button asChild><span>{t('text1148')}</span></Button>
    </Link>
  );
}
